/* eslint-disable @typescript-eslint/naming-convention */
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { DateRangeType } from 'components/DatePicker/useDatePicker'
import { Product, SkuRegistration } from 'features/Product/@types'
import { GetActiveProductCategoriesType } from 'features/Product/@types/Product'
import { BrandItemType, GetBrandDetailByIdResponseType } from 'features/Product/services/brand'
import {
  ProductTypeItemType,
  GetProductAttributeOptionResponseType,
  StorageHandlingType,
} from 'features/Product/@types/ProductAdd'
import { GetSkuRegistrationListParamsType } from 'features/Product/services/skuRegistration'
import {
  ClusterItemType,
  GetKongLocationAllResponseType,
  GetLocationDetailListType,
} from 'features/Location/@types/location'
import { DiscoveryLabelType } from 'utils/apiList/dicoveryLabel'
import {
  initialStateGeneralDataForm,
  initialStateHandlingDataForm,
  initialStatePricingForm,
  initialStateAdditionalForm,
  initialStateSellingDetailForm,
  initialStateExchangeProductForm,
  initialStateAssortmentDetailForm,
  initialStateAssortmentPlanningForm,
  initialStateSkuAssetForm,
  initialErrorGeneralForm,
  initialErrorHandlingForm,
  initialErrorPricingForm,
  initialStateErrorAdditionalForm,
  initialStateErrorSellingDetailForm,
  initialStateErrorSkuPortfolio,
  initialStateFailedDataCreationForm,
  initialStateBase,
  initialStatePrimarySecondary,
  initialStateInnerOuter,
} from './initialState'

export type LocationListType = {
  id: number
  name: string
  type: string
}

export type ProductImageType = {
  id: string
  file: Nullable<File>
  previewUrl: string
  pathLarge: string
  pathMedium: string
  pathSmall: string
}

export type UomListType = Pick<
  Product.GetUomResponseType['unit_of_measurements']['content'][number],
  'id' | 'description'
> & {
  label: string
}

export type BrandListType = (BrandItemType & { label: string })[]

export type ModalSelectLocationType = {
  isOpen: boolean
  pageSize: number
  currentPage: number
  searchingInput: string
  source: string
  selectedLocationType: string
  selectedLocationDetailType: Nullable<GetLocationDetailListType['data'][number]>
  selectedLocationIds: number[]
  selectedLocation: {
    id: number
    name: string
    type: string
  }[]
  locationTypeByBusinessTagging: string[]
  locationList: GetKongLocationAllResponseType['location']
  locationDetailList: GetLocationDetailListType['data']
  removeByType: string[]
}

export type SkuRegistrationInitialStateType = {
  selectedId: string
  paginationData: PaginationType
  skuRegistrationList: SkuRegistration.GetSkuRegistrationListResponseType['data']
  shouldClearSearchKeyword: boolean
  params: GetSkuRegistrationListParamsType
  selectedStatus: Nullable<SkuRegistration.ConstantItemType>
  selectedDateRange: {
    from: Date | null
    to: Date | null
  }
  selectedCreator: Nullable<
    SkuRegistration.GetCreatorsSkuRegistrationResponseType['data']['users'][number]
  >
  creatorList: SkuRegistration.GetCreatorsSkuRegistrationResponseType['data']['users']
  vendorList: {
    id: number
    company_name: string
  }[]
  brandList: BrandListType
  brandDetail: Nullable<GetBrandDetailByIdResponseType>
  businessTaggingList: GetProductAttributeOptionResponseType['data']
  productTypeList: ProductTypeItemType[]
  categoryL1List: GetActiveProductCategoriesType['categories']
  categoryL2List: GetActiveProductCategoriesType['categories'][number]['children']
  selectedCategoryL1: Nullable<GetActiveProductCategoriesType['categories'][number]>
  selectedCategoryL2: Nullable<
    GetActiveProductCategoriesType['categories'][number]['children'][number]
  >
  constant: SkuRegistration.GetAllConstantType
  skuRegistrationDetail: Nullable<SkuRegistration.SkuRegistrationItemType>
  labelImageList: DiscoveryLabelType[]
  labelPromotionList: DiscoveryLabelType[]
  modalUpdateSku: {
    isOpen: boolean
    isSubmittingData: boolean
    selectedTab: string
    isAutoGenerateBarcode: boolean
    selectedSpecialHandlingDetail: {
      key: string
      label: string
    }[]
    generalDataForm: SkuRegistration.GeneralDataType
    handlingDataForm: SkuRegistration.HandlingType
    grammationData: Nullable<SkuRegistration.GrammationSubmitV2Type>
    pricingForm: SkuRegistration.PricingType
    errorGeneralForm: SkuRegistration.ErrorGeneralFormType
    errorHandlingForm: SkuRegistration.ErrorHandlingFormType
    errorPricingForm: SkuRegistration.ErrorPricingFormType
  }
  modalAdditionalDetails: {
    isOpen: boolean
    isSubmittingData: boolean
    selectedTab: string
    selectedPromotionTermPeriod: DateRangeType['value']
    errorAdditionalForm: SkuRegistration.ErrorAdditionalFormType
    errorSellingDetailForm: SkuRegistration.ErrorSellingDetailFormType
    additionalForm: SkuRegistration.AdditionalInformationType
    sellingDetailForm: SkuRegistration.SellingDetailType
  }
  modalApproveSku: {
    isOpen: boolean
    selectedTab: string
    isRejecting: {
      generalData: boolean
      customFieldsOnGeneralData: string[]
      handling: boolean
      grammation: boolean
      pricing: boolean
      additionalData: boolean
      customFieldsOnAdditionalData: string[]
      sellingDetail: boolean
      exchangeProduct: boolean
      assortmentDetail: boolean
      assortmentPlanning: boolean
      skuAsset: boolean
    }
    additionalFlaggingRejected: boolean
    error: {
      reject_reason: string
    }
    isSubmittingData: boolean
  }
  isOpenModalRejectReason: boolean
  isOpenModalCancelSkuRegis: boolean
  modalSkuPortfolio: {
    isOpen: boolean
    isSubmittingData: boolean
    selectedTab: string
    errorSkuPortfolio: SkuRegistration.ErrorSkuPortfolioType
    isDisabledClusterOrHubLevel: boolean
    exchangeProductForm: SkuRegistration.ProductSubtitutionType
    assortmentDetailForm: SkuRegistration.AssortmentDetailType
    assortmentPlanningForm: SkuRegistration.AssortmentPlanningType
    selectedCluster: ClusterItemType[]
    isOpenModalLocationByCluster: boolean
  }
  modalSkuMerchandising: {
    isOpen: boolean
    isSubmittingData: boolean
    needToInitializeData: boolean
    isEditableMuiAndHalal: boolean
    selectedTab: string
    productImageList: ProductImageType[]
    productDescription: string
    errorSkuAssetForm: {
      image_paths: string
      name: string
      description: string
    }
    errorGeneralData: {
      mui: string
    }
    skuAssetForm: SkuRegistration.AssetType
    generalData: Nullable<SkuRegistration.GeneralDataSubmitType>
    additionalInformationData: Nullable<SkuRegistration.AdditionalInformationSubmitType>
  }
  modalFailedDataCreation: {
    isOpen: boolean
    isSubmittingData: boolean
    failedDataCreationForm: SkuRegistration.FailedMasterDataCreationType
    errorFailedDataCreation: SkuRegistration.ErrorFailedDataCreationType
  }
  productExchangeInfoDetail: Nullable<Product.ProductExchangeInfoDetailType>
  modalConfirmSubmit: {
    isOpen: boolean
    source: string
  }
  itemUomList: UomListType[]
  volumeUomList: UomListType[]
  weightUomList: UomListType[]
  lengthUomList: UomListType[]
  productList: Product.ProductListType[]
  hubLevelList: { id: number; name: string; type: string }[]
  clusterList: ClusterItemType[]
  storageHandlingList: StorageHandlingType[]
  modalLocationForInboundWhDestination: ModalSelectLocationType
  modalSelectLocation: ModalSelectLocationType
  modalSelectLocationSource: string
}

const initialState: SkuRegistrationInitialStateType = {
  selectedId: '',
  paginationData: {
    direction: 'desc',
    page_size: 20,
    number_of_elements: 0,
    total_elements: 0,
    sort: '',
    page_index: 1,
  },
  skuRegistrationList: [],
  shouldClearSearchKeyword: false,
  params: {
    'pagination.page_index': 1,
    'pagination.page_size': 20,
    'pagination.direction': 'asc',
  },
  selectedStatus: null,
  selectedDateRange: {
    from: null,
    to: null,
  },
  selectedCreator: null,
  creatorList: [],
  vendorList: [],
  brandList: [],
  brandDetail: null,
  businessTaggingList: [],
  productTypeList: [],
  categoryL1List: [],
  categoryL2List: [],
  selectedCategoryL1: null,
  selectedCategoryL2: null,
  constant: {
    product_special_handling: [],
    product_registration_statuses: [],
    product_shelves: [],
    product_image_host: '',
    social_media_list: [],
    pap_location_types: [],
    product_ppn: [],
    product_type_uom: [],
  },
  skuRegistrationDetail: null,
  labelImageList: [],
  labelPromotionList: [],
  modalUpdateSku: {
    isOpen: false,
    isSubmittingData: false,
    selectedTab: 'generalData',
    isAutoGenerateBarcode: false,
    selectedSpecialHandlingDetail: [],
    generalDataForm: initialStateGeneralDataForm,
    handlingDataForm: initialStateHandlingDataForm,
    grammationData: null,
    pricingForm: initialStatePricingForm,
    errorGeneralForm: initialErrorGeneralForm,
    errorHandlingForm: initialErrorHandlingForm,
    errorPricingForm: initialErrorPricingForm,
  },
  modalAdditionalDetails: {
    isOpen: false,
    isSubmittingData: false,
    selectedTab: 'additionalData',
    selectedPromotionTermPeriod: [null, null],
    errorAdditionalForm: initialStateErrorAdditionalForm,
    errorSellingDetailForm: initialStateErrorSellingDetailForm,
    additionalForm: initialStateAdditionalForm,
    sellingDetailForm: initialStateSellingDetailForm,
  },
  modalApproveSku: {
    isOpen: false,
    selectedTab: '',
    isRejecting: {
      generalData: false,
      customFieldsOnGeneralData: [''],
      handling: false,
      grammation: false,
      pricing: false,
      additionalData: false,
      customFieldsOnAdditionalData: [''],
      sellingDetail: false,
      exchangeProduct: false,
      assortmentDetail: false,
      assortmentPlanning: false,
      skuAsset: false,
    },
    additionalFlaggingRejected: false,
    error: {
      reject_reason: '',
    },
    isSubmittingData: false,
  },
  isOpenModalRejectReason: false,
  isOpenModalCancelSkuRegis: false,
  modalSkuPortfolio: {
    isOpen: false,
    isSubmittingData: false,
    selectedTab: 'exchangeProduct',
    errorSkuPortfolio: initialStateErrorSkuPortfolio,
    isDisabledClusterOrHubLevel: false,
    exchangeProductForm: initialStateExchangeProductForm,
    assortmentDetailForm: initialStateAssortmentDetailForm,
    assortmentPlanningForm: initialStateAssortmentPlanningForm,
    selectedCluster: [],
    isOpenModalLocationByCluster: false,
  },
  modalSkuMerchandising: {
    isOpen: false,
    isSubmittingData: false,
    needToInitializeData: false,
    isEditableMuiAndHalal: false,
    selectedTab: 'skuAsset',
    productImageList: [],
    productDescription: '',
    errorSkuAssetForm: {
      image_paths: '',
      name: '',
      description: '',
    },
    errorGeneralData: {
      mui: '',
    },
    skuAssetForm: initialStateSkuAssetForm,
    generalData: null,
    additionalInformationData: null,
  },
  modalFailedDataCreation: {
    isOpen: false,
    isSubmittingData: false,
    failedDataCreationForm: initialStateFailedDataCreationForm,
    errorFailedDataCreation: {
      product_name: '',
      primary_grammation_barcode: '',
      secondary_grammation_barcode: '',
    },
  },
  productExchangeInfoDetail: null,
  modalConfirmSubmit: {
    isOpen: false,
    source: '',
  },
  itemUomList: [],
  volumeUomList: [],
  weightUomList: [],
  lengthUomList: [],
  productList: [],
  hubLevelList: [],
  clusterList: [],
  storageHandlingList: [],
  modalLocationForInboundWhDestination: {
    isOpen: false,
    pageSize: 40,
    currentPage: 0,
    searchingInput: '',
    source: 'inboundWh',
    selectedLocationType: '',
    selectedLocationDetailType: null,
    locationTypeByBusinessTagging: [],
    selectedLocationIds: [],
    selectedLocation: [],
    locationList: [],
    locationDetailList: [],
    removeByType: [],
  },
  modalSelectLocation: {
    isOpen: false,
    pageSize: 40,
    currentPage: 0,
    searchingInput: '',
    source: 'proposedHub',
    selectedLocationType: '',
    selectedLocationDetailType: null,
    locationTypeByBusinessTagging: [],
    selectedLocationIds: [],
    selectedLocation: [],
    locationList: [],
    locationDetailList: [],
    removeByType: [],
  },
  modalSelectLocationSource: '',
}

const skuRegistration = createSlice({
  name: 'skuRegistration',
  initialState,
  reducers: {
    resetState: () => initialState,
    setSelectedId: (state, action: PayloadAction<string>) => {
      state.selectedId = action.payload
    },
    setShouldClearSearchKeyword: (state, action: PayloadAction<boolean>) => {
      state.shouldClearSearchKeyword = action.payload
    },
    setParams: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['params']>>,
    ) => {
      state.params = {
        ...state.params,
        ...action.payload,
      }
    },
    resetParams: (state) => {
      state.params = initialState.params
    },
    setSelectedStatus: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['selectedStatus']>,
    ) => {
      state.selectedStatus = action.payload
    },
    resetSelectedStatus: (state) => {
      state.selectedStatus = initialState.selectedStatus
    },
    setSelectedCategoryL1: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['selectedCategoryL1']>,
    ) => {
      state.selectedCategoryL1 = action.payload
    },
    setSelectedCategoryL2: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['selectedCategoryL2']>,
    ) => {
      state.selectedCategoryL2 = action.payload
    },
    setSkuRegistrationDetailOnFetchSuccess: (
      state,
      action: PayloadAction<SkuRegistration.SkuRegistrationItemType>,
    ) => {
      state.skuRegistrationDetail = action.payload
    },
    setSkuRegistrationDetail: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail = {
          ...state.skuRegistrationDetail,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnGeneralData: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['general_data']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.general_data = {
          ...state.skuRegistrationDetail.general_data,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnHandlingData: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['handling']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.handling = {
          ...state.skuRegistrationDetail.handling,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnGrammationBase: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['grammation']['base']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        if (state.skuRegistrationDetail.grammation.base) {
          state.skuRegistrationDetail.grammation.base = {
            ...state.skuRegistrationDetail.grammation.base,
            ...action.payload,
          }
        } else {
          state.skuRegistrationDetail.grammation.base = {
            ...initialStateBase,
            ...action.payload,
          }
        }
      }
    },
    updateIsRejectedOnGrammationInnerBox: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['grammation']['inner_box']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        if (state.skuRegistrationDetail.grammation.inner_box) {
          state.skuRegistrationDetail.grammation.inner_box = {
            ...state.skuRegistrationDetail.grammation.inner_box,
            ...action.payload,
          }
        } else {
          state.skuRegistrationDetail.grammation.inner_box = {
            ...initialStateInnerOuter,
            ...action.payload,
          }
        }
      }
    },
    updateIsRejectedOnGrammationOuterBox: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['grammation']['outer_box']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        if (state.skuRegistrationDetail.grammation.outer_box) {
          state.skuRegistrationDetail.grammation.outer_box = {
            ...state.skuRegistrationDetail.grammation.outer_box,
            ...action.payload,
          }
        } else {
          state.skuRegistrationDetail.grammation.outer_box = {
            ...initialStateInnerOuter,
            ...action.payload,
          }
        }
      }
    },
    updateIsRejectedOnGrammationPrimary: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['grammation']['primary']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        if (state.skuRegistrationDetail.grammation.primary) {
          state.skuRegistrationDetail.grammation.primary = {
            ...state.skuRegistrationDetail.grammation.primary,
            ...action.payload,
          }
        } else {
          state.skuRegistrationDetail.grammation.primary = {
            ...initialStatePrimarySecondary,
            ...action.payload,
          }
        }
      }
    },
    updateIsRejectedOnGrammationSecondary: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['grammation']['secondary']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        if (state.skuRegistrationDetail.grammation.secondary) {
          state.skuRegistrationDetail.grammation.secondary = {
            ...state.skuRegistrationDetail.grammation.secondary,
            ...action.payload,
          }
        } else {
          state.skuRegistrationDetail.grammation.secondary = {
            ...initialStatePrimarySecondary,
            ...action.payload,
          }
        }
      }
    },
    updateIsRejectedOnGrammation: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['grammation']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.grammation = {
          ...state.skuRegistrationDetail.grammation,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnPricing: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['pricing']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.pricing = {
          ...state.skuRegistrationDetail.pricing,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnAdditionalData: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['additional_information']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.additional_information = {
          ...state.skuRegistrationDetail.additional_information,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnSellingDetail: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['selling_detail']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.selling_detail = {
          ...state.skuRegistrationDetail.selling_detail,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnSkuAsset: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['asset']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.asset = {
          ...state.skuRegistrationDetail.asset,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnExchangeProduct: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['product_substitution']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.product_substitution = {
          ...state.skuRegistrationDetail.product_substitution,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnAssortmentDetail: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SkuRegistrationItemType['assortment_detail']>>,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.assortment_detail = {
          ...state.skuRegistrationDetail.assortment_detail,
          ...action.payload,
        }
      }
    },
    updateIsRejectedOnAssortmentPlanning: (
      state,
      action: PayloadAction<
        Partial<SkuRegistration.SkuRegistrationItemType['assortment_planning']>
      >,
    ) => {
      if (state.skuRegistrationDetail) {
        state.skuRegistrationDetail.assortment_planning = {
          ...state.skuRegistrationDetail.assortment_planning,
          ...action.payload,
        }
      }
    },
    setBusinessTaggingList: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['businessTaggingList']>,
    ) => {
      state.businessTaggingList = action.payload
    },
    setProductTypeList: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['productTypeList']>,
    ) => {
      state.productTypeList = action.payload
    },
    setCategories: (state, action) => {
      const { type, listData } = action.payload
      if (type === 'L1') {
        state.categoryL1List = listData
      } else if (type === 'L2') {
        state.categoryL2List = listData
      }
    },
    setConstant: (state, action: PayloadAction<SkuRegistration.GetAllConstantType>) => {
      state.constant = action.payload
    },
    setSkuRegistrationList: (
      state,
      action: PayloadAction<{
        data: SkuRegistrationInitialStateType['skuRegistrationList']
        pagination: SkuRegistrationInitialStateType['paginationData']
      }>,
    ) => {
      const { data, pagination } = action.payload
      state.skuRegistrationList = data
      state.paginationData = pagination
    },
    setVendorList: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['vendorList']>,
    ) => {
      state.vendorList = action.payload
    },
    setClusterList: (state, action: PayloadAction<ClusterItemType[]>) => {
      state.clusterList = action.payload
    },
    setStorageHandlingList: (state, action: PayloadAction<StorageHandlingType[]>) => {
      state.storageHandlingList = action.payload
    },
    setBrandList: (state, action: PayloadAction<SkuRegistrationInitialStateType['brandList']>) => {
      state.brandList = action.payload
    },
    setBrandDetail: (state, action: PayloadAction<Nullable<GetBrandDetailByIdResponseType>>) => {
      state.brandDetail = action.payload
    },
    setModalUpdateSku: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalUpdateSku']>>,
    ) => {
      state.modalUpdateSku = {
        ...state.modalUpdateSku,
        ...action.payload,
      }
    },
    resetModalUpdateSku: (state) => {
      state.modalUpdateSku = initialState.modalUpdateSku
    },
    setSelectedDateRange: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['selectedDateRange']>,
    ) => {
      state.selectedDateRange = action.payload
    },
    resetSelectedDateRange: (state) => {
      state.selectedDateRange = initialState.selectedDateRange
    },
    setSelectedCreator: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['selectedCreator']>,
    ) => {
      state.selectedCreator = action.payload
    },
    setCreatorList: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['creatorList']>,
    ) => {
      state.creatorList = action.payload
    },
    setErrorGeneralForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.ErrorGeneralFormType>>,
    ) => {
      state.modalUpdateSku.errorGeneralForm = {
        ...state.modalUpdateSku.errorGeneralForm,
        ...action.payload,
      }
    },
    setErrorHandlingForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.ErrorHandlingFormType>>,
    ) => {
      state.modalUpdateSku.errorHandlingForm = {
        ...state.modalUpdateSku.errorHandlingForm,
        ...action.payload,
      }
    },
    setErrorPricingForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.ErrorPricingFormType>>,
    ) => {
      state.modalUpdateSku.errorPricingForm = {
        ...state.modalUpdateSku.errorPricingForm,
        ...action.payload,
      }
    },
    setGeneralDataForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.GeneralDataType>>,
    ) => {
      state.modalUpdateSku.generalDataForm = {
        ...state.modalUpdateSku.generalDataForm,
        ...action.payload,
      }
    },
    setHandlingDataForm: (state, action: PayloadAction<Partial<SkuRegistration.HandlingType>>) => {
      state.modalUpdateSku.handlingDataForm = {
        ...state.modalUpdateSku.handlingDataForm,
        ...action.payload,
      }
    },
    setPricingForm: (state, action: PayloadAction<Partial<SkuRegistration.PricingType>>) => {
      state.modalUpdateSku.pricingForm = {
        ...state.modalUpdateSku.pricingForm,
        ...action.payload,
      }
    },
    setAdditionalForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.AdditionalInformationType>>,
    ) => {
      state.modalAdditionalDetails.additionalForm = {
        ...state.modalAdditionalDetails.additionalForm,
        ...action.payload,
      }
    },
    setSellingDetailForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.SellingDetailType>>,
    ) => {
      state.modalAdditionalDetails.sellingDetailForm = {
        ...state.modalAdditionalDetails.sellingDetailForm,
        ...action.payload,
      }
    },
    setErrorAdditionalForm: (
      state,
      action: PayloadAction<
        Partial<SkuRegistrationInitialStateType['modalAdditionalDetails']['errorAdditionalForm']>
      >,
    ) => {
      state.modalAdditionalDetails.errorAdditionalForm = {
        ...state.modalAdditionalDetails.errorAdditionalForm,
        ...action.payload,
      }
    },
    setErrorSellingDetailForm: (
      state,
      action: PayloadAction<
        Partial<SkuRegistrationInitialStateType['modalAdditionalDetails']['errorSellingDetailForm']>
      >,
    ) => {
      state.modalAdditionalDetails.errorSellingDetailForm = {
        ...state.modalAdditionalDetails.errorSellingDetailForm,
        ...action.payload,
      }
    },
    setHubLevelList: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['hubLevelList']>,
    ) => {
      state.hubLevelList = action.payload
    },
    setModalAdditionalDetails: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalAdditionalDetails']>>,
    ) => {
      state.modalAdditionalDetails = {
        ...state.modalAdditionalDetails,
        ...action.payload,
      }
    },
    resetModalAdditionalDetails: (state) => {
      state.modalAdditionalDetails = initialState.modalAdditionalDetails
    },
    setModalApproveSku: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalApproveSku']>>,
    ) => {
      state.modalApproveSku = {
        ...state.modalApproveSku,
        ...action.payload,
      }
    },
    resetIsRejectingModalApproveSku: (state) => {
      state.modalApproveSku.isRejecting = initialState.modalApproveSku.isRejecting
    },
    setIsOpenModalRejectReason: (state, action: PayloadAction<boolean>) => {
      state.isOpenModalRejectReason = action.payload
    },
    resetModalApproveSku: (state) => {
      state.modalApproveSku = initialState.modalApproveSku
    },
    setErrorSkuPortfolio: (
      state,
      action: PayloadAction<
        Partial<SkuRegistrationInitialStateType['modalSkuPortfolio']['errorSkuPortfolio']>
      >,
    ) => {
      state.modalSkuPortfolio.errorSkuPortfolio = {
        ...state.modalSkuPortfolio.errorSkuPortfolio,
        ...action.payload,
      }
    },
    setModalSkuPortfolio: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalSkuPortfolio']>>,
    ) => {
      state.modalSkuPortfolio = {
        ...state.modalSkuPortfolio,
        ...action.payload,
      }
    },
    resetModalSkuPortfolio: (state) => {
      state.modalSkuPortfolio = initialState.modalSkuPortfolio
    },
    setExchangeProductFormData: (
      state,
      action: PayloadAction<Partial<SkuRegistration.ProductSubtitutionType>>,
    ) => {
      state.modalSkuPortfolio.exchangeProductForm = {
        ...state.modalSkuPortfolio.exchangeProductForm,
        ...action.payload,
      }
    },
    setAssortmentDetailForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.AssortmentDetailType>>,
    ) => {
      state.modalSkuPortfolio.assortmentDetailForm = {
        ...state.modalSkuPortfolio.assortmentDetailForm,
        ...action.payload,
      }
    },
    setAssortmentPlanningForm: (
      state,
      action: PayloadAction<Partial<SkuRegistration.AssortmentPlanningType>>,
    ) => {
      state.modalSkuPortfolio.assortmentPlanningForm = {
        ...state.modalSkuPortfolio.assortmentPlanningForm,
        ...action.payload,
      }
    },
    setLabelList: (
      state,
      action: PayloadAction<{
        fieldName: 'labelImageList' | 'labelPromotionList'
        listData: DiscoveryLabelType[]
      }>,
    ) => {
      const { fieldName, listData } = action.payload
      state[fieldName] = listData
    },
    setProductImageList: (
      state,
      action: PayloadAction<
        SkuRegistrationInitialStateType['modalSkuMerchandising']['productImageList']
      >,
    ) => {
      state.modalSkuMerchandising.productImageList = action.payload
    },
    setErrorSkuAssetForm: (
      state,
      action: PayloadAction<
        Partial<SkuRegistrationInitialStateType['modalSkuMerchandising']['errorSkuAssetForm']>
      >,
    ) => {
      state.modalSkuMerchandising.errorSkuAssetForm = {
        ...state.modalSkuMerchandising.errorSkuAssetForm,
        ...action.payload,
      }
    },
    setErrorGeneralDataInModalSkuMerchandising: (
      state,
      action: PayloadAction<
        Partial<SkuRegistrationInitialStateType['modalSkuMerchandising']['errorGeneralData']>
      >,
    ) => {
      state.modalSkuMerchandising.errorGeneralData = {
        ...state.modalSkuMerchandising.errorGeneralData,
        ...action.payload,
      }
    },
    setModalSkuMerchandising: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalSkuMerchandising']>>,
    ) => {
      state.modalSkuMerchandising = {
        ...state.modalSkuMerchandising,
        ...action.payload,
      }
    },
    resetModalSkuMerchandising: (state) => {
      state.modalSkuMerchandising = initialState.modalSkuMerchandising
    },
    setSkuAssetForm: (state, action: PayloadAction<Partial<SkuRegistration.AssetType>>) => {
      state.modalSkuMerchandising.skuAssetForm = {
        ...state.modalSkuMerchandising.skuAssetForm,
        ...action.payload,
      }
    },
    setGeneralDataInModalSkuMerchandising: (
      state,
      action: PayloadAction<Partial<SkuRegistration.GeneralDataSubmitType>>,
    ) => {
      if (!state.modalSkuMerchandising.generalData) {
        state.modalSkuMerchandising.generalData =
          action.payload as SkuRegistration.GeneralDataSubmitType
      } else {
        state.modalSkuMerchandising.generalData = {
          ...state.modalSkuMerchandising.generalData,
          ...action.payload,
        }
      }
    },
    setAdditionalInformationDataInModalSkuMerchandising: (
      state,
      action: PayloadAction<Partial<SkuRegistration.AdditionalInformationSubmitType>>,
    ) => {
      if (!state.modalSkuMerchandising.additionalInformationData) {
        state.modalSkuMerchandising.additionalInformationData =
          action.payload as SkuRegistration.AdditionalInformationSubmitType
      } else {
        state.modalSkuMerchandising.additionalInformationData = {
          ...state.modalSkuMerchandising.additionalInformationData,
          ...action.payload,
        }
      }
    },
    setUomList: (
      state,
      action: PayloadAction<{
        type: Product.UomGroupType
        listData: UomListType[]
      }>,
    ) => {
      const { type, listData } = action.payload
      if (type === 'ITEM') {
        state.itemUomList = listData
      } else if (type === 'VOLUME') {
        state.volumeUomList = listData
      } else if (type === 'WEIGHT') {
        state.weightUomList = listData
      } else if (type === 'LENGTH') {
        state.lengthUomList = listData
      }
    },
    setProductExchangeInfoDetail: (
      state,
      action: PayloadAction<Nullable<Product.ProductExchangeInfoDetailType>>,
    ) => {
      state.productExchangeInfoDetail = action.payload
    },
    setProductList: (state, action: PayloadAction<Product.ProductListType[]>) => {
      state.productList = action.payload
    },
    setIsOpenModalCancelSkuRegis: (state, action: PayloadAction<boolean>) => {
      state.isOpenModalCancelSkuRegis = action.payload
    },
    setModalConfirmSubmit: (
      state,
      action: PayloadAction<SkuRegistrationInitialStateType['modalConfirmSubmit']>,
    ) => {
      state.modalConfirmSubmit = {
        ...state.modalConfirmSubmit,
        ...action.payload,
      }
    },
    setModalFailedDataCreation: (
      state,
      action: PayloadAction<Partial<SkuRegistrationInitialStateType['modalFailedDataCreation']>>,
    ) => {
      state.modalFailedDataCreation = {
        ...state.modalFailedDataCreation,
        ...action.payload,
      }
    },
    setFailedDataCreationForm: (
      state,
      action: PayloadAction<
        Partial<
          SkuRegistrationInitialStateType['modalFailedDataCreation']['failedDataCreationForm']
        >
      >,
    ) => {
      state.modalFailedDataCreation.failedDataCreationForm = {
        ...state.modalFailedDataCreation.failedDataCreationForm,
        ...action.payload,
      }
    },
    setErrorFailedDataCreation: (
      state,
      action: PayloadAction<Partial<SkuRegistration.ErrorFailedDataCreationType>>,
    ) => {
      state.modalFailedDataCreation.errorFailedDataCreation = {
        ...state.modalFailedDataCreation.errorFailedDataCreation,
        ...action.payload,
      }
    },
    resetModalFailedDataCreation: (state) => {
      state.modalFailedDataCreation = initialState.modalFailedDataCreation
    },
    setModalLocationForInboundWhDestination: (
      state,
      action: PayloadAction<Partial<ModalSelectLocationType>>,
    ) => {
      state.modalLocationForInboundWhDestination = {
        ...state.modalLocationForInboundWhDestination,
        ...action.payload,
      }
    },
    setModalSelectLocation: (state, action: PayloadAction<Partial<ModalSelectLocationType>>) => {
      state.modalSelectLocation = {
        ...state.modalSelectLocation,
        ...action.payload,
      }
    },
    resetModalSelectLocation: (state) => {
      state.modalSelectLocation = initialState.modalSelectLocation
    },
    manageSelectedLocationOfModalSelectLocation: (
      state,
      action: PayloadAction<GetKongLocationAllResponseType['location'][number]>,
    ) => {
      const location = action.payload
      const locationId = location.location_id

      const modalStateName =
        state.modalSelectLocationSource === 'inboundWh'
          ? 'modalLocationForInboundWhDestination'
          : 'modalSelectLocation'

      if (state[modalStateName].selectedLocationIds.includes(locationId)) {
        const listIds = state[modalStateName].selectedLocationIds.filter((id) => id !== locationId)
        const listLocation = state[modalStateName].selectedLocation.filter(
          (item) => item.id !== locationId,
        )
        state[modalStateName].selectedLocationIds = listIds
        state[modalStateName].selectedLocation = listLocation
      } else {
        state[modalStateName].selectedLocationIds.push(locationId)
        state[modalStateName].selectedLocation.push({
          id: location.location_id,
          name: location.location_name,
          type: location.location_type,
        })
      }
    },
    toggleAllLocationsBySelectedLocationType: (state) => {
      const modalStateName =
        state.modalSelectLocationSource === 'inboundWh'
          ? 'modalLocationForInboundWhDestination'
          : 'modalSelectLocation'
      state[modalStateName].locationList.forEach((locationItem) => {
        if (!state[modalStateName].selectedLocationIds.includes(locationItem.location_id)) {
          state[modalStateName].selectedLocationIds.push(locationItem.location_id)
          state[modalStateName].selectedLocation.push({
            id: locationItem.location_id,
            name: locationItem.location_name,
            type: locationItem.location_type,
          })
        }
      })
    },
    setModalSelectLocationSource: (state, action) => {
      state.modalSelectLocationSource = action.payload
    },
  },
})

export const {
  resetState,
  setSelectedId,
  setShouldClearSearchKeyword,
  setParams,
  resetParams,
  setSelectedStatus,
  resetSelectedStatus,
  setSelectedCategoryL1,
  setSelectedCategoryL2,
  setSkuRegistrationDetailOnFetchSuccess,
  setSkuRegistrationDetail,
  updateIsRejectedOnGeneralData,
  updateIsRejectedOnHandlingData,
  updateIsRejectedOnGrammationBase,
  updateIsRejectedOnGrammation,
  updateIsRejectedOnGrammationInnerBox,
  updateIsRejectedOnGrammationOuterBox,
  updateIsRejectedOnGrammationPrimary,
  updateIsRejectedOnGrammationSecondary,
  updateIsRejectedOnPricing,
  updateIsRejectedOnAdditionalData,
  updateIsRejectedOnSellingDetail,
  updateIsRejectedOnSkuAsset,
  updateIsRejectedOnExchangeProduct,
  updateIsRejectedOnAssortmentDetail,
  updateIsRejectedOnAssortmentPlanning,
  setCategories,
  setConstant,
  setSkuRegistrationList,
  setVendorList,
  setClusterList,
  setStorageHandlingList,
  setBrandList,
  setBrandDetail,
  setModalUpdateSku,
  resetModalUpdateSku,
  setSelectedDateRange,
  resetSelectedDateRange,
  setSelectedCreator,
  setCreatorList,
  setErrorGeneralForm,
  setErrorHandlingForm,
  setErrorPricingForm,
  setGeneralDataForm,
  setHandlingDataForm,
  setPricingForm,
  setAdditionalForm,
  setSellingDetailForm,
  setErrorAdditionalForm,
  setErrorSellingDetailForm,
  setHubLevelList,
  setModalAdditionalDetails,
  resetModalAdditionalDetails,
  setModalApproveSku,
  resetIsRejectingModalApproveSku,
  setIsOpenModalRejectReason,
  resetModalApproveSku,
  setErrorSkuPortfolio,
  setModalSkuPortfolio,
  resetModalSkuPortfolio,
  setExchangeProductFormData,
  setAssortmentDetailForm,
  setAssortmentPlanningForm,
  setLabelList,
  setProductImageList,
  setErrorSkuAssetForm,
  setModalSkuMerchandising,
  resetModalSkuMerchandising,
  setSkuAssetForm,
  setGeneralDataInModalSkuMerchandising,
  setErrorGeneralDataInModalSkuMerchandising,
  setAdditionalInformationDataInModalSkuMerchandising,
  setUomList,
  setProductExchangeInfoDetail,
  setProductList,
  setBusinessTaggingList,
  setProductTypeList,
  setIsOpenModalCancelSkuRegis,
  setModalConfirmSubmit,
  setModalFailedDataCreation,
  setFailedDataCreationForm,
  setErrorFailedDataCreation,
  resetModalFailedDataCreation,
  setModalSelectLocation,
  resetModalSelectLocation,
  manageSelectedLocationOfModalSelectLocation,
  toggleAllLocationsBySelectedLocationType,
  setModalLocationForInboundWhDestination,
  setModalSelectLocationSource,
} = skuRegistration.actions
export default skuRegistration.reducer
